import { getCurrentLanguage } from '../config/languageConfig';
import { SupportedCurrency } from './currencyUtils';
import { CurationStore } from '../types/curation';

/**
 * 평점 갯수를 간략한 형태로 포맷팅
 * 예: 5 → "5", 15 → "10+", 60 → "50+", 150 → "100+", 650 → "500+", 1500 → "1K+"
 */
export const formatReviewCount = (count: number): string => {
  if (count >= 1000) {
    const thousands = Math.floor(count / 1000);
    return `${thousands}K+`;
  } else if (count >= 500) {
    return '500+';
  } else if (count >= 100) {
    return '100+';
  } else if (count >= 50) {
    return '50+';
  } else if (count >= 10) {
    return '10+';
  }
  return count.toString();
};

/**
 * 평점을 소수점 한 자리로 포맷팅
 * 예: 4.567 → "4.6", 4.0 → "4.0"
 */
export const formatRating = (rating: number): string => {
  return rating.toFixed(1);
};

/**
 * 거리를 적절한 단위로 포맷팅
 * 50m 미만은 "근처", 0.1km 미만은 소수점 두 자리, 이상은 한 자리로 표시
 * 예: 0.000002831 → "근처", 0.04595382 → "0.05km", 1.9391158 → "1.9km"
 */
export const formatDistance = (distance: number, t: (key: string) => string): string => {
  // 50m 미만 (0.05km 미만)은 "근처"로 표시
  if (distance < 0.05) {
    return t('curation.nearby');
  }
  
  // 0.1km 미만은 소수점 두 자리
  if (distance < 0.1) {
    return `${distance.toFixed(2)}km`;
  }
  
  // 0.1km 이상은 소수점 한 자리
  return `${distance.toFixed(1)}km`;
};

/**
 * 큐레이션 스토어의 다국어 이름 가져오기
 */
export const getCurationStoreLocalizedName = (store: CurationStore): string => {
  const currentLang = getCurrentLanguage();
  
  switch (currentLang) {
    case 'ko':
      return store.nameKo || store.nameEn || store.name;
    case 'ja':
      return store.nameJa || store.nameEn || store.name;
    case 'zh-CN':
      return store.nameZhCn || store.nameEn || store.name;
    case 'zh-TW':
      return store.nameZhTw || store.nameEn || store.name;
    case 'en':
    default:
      return store.nameEn || store.name;
  }
};

/**
 * 큐레이션 스토어의 통화별 최소주문금액 가져오기
 */
export const getCurationStoreMinOrderAmount = (store: CurationStore, currency: SupportedCurrency): number => {
  switch (currency) {
    case 'KRW':
      return store.minOrderAmountKrw;
    case 'USD':
      return store.minOrderAmountUsd;
    case 'JPY':
      return store.minOrderAmountJpy;
    case 'CNY':
      return store.minOrderAmountCny;
    case 'TWD':
      return store.minOrderAmountTwd;
    default:
      return store.minOrderAmount;
  }
};

/**
 * 통화 기호 가져오기
 */
export const getCurrencySymbol = (currency: SupportedCurrency): string => {
  switch (currency) {
    case 'KRW':
      return '₩';
    case 'USD':
      return '$';
    case 'JPY':
      return '¥';
    case 'CNY':
      return '¥';
    case 'TWD':
      return 'NT$';
    default:
      return '$';
  }
};

/**
 * 최소주문금액을 통화별로 포맷팅 (formatPrice와 동일한 로직 사용)
 */
export const formatMinOrderAmount = (store: CurationStore, currency: SupportedCurrency): string => {
  const amount = getCurationStoreMinOrderAmount(store, currency);
  
  // formatPrice와 동일한 로직 사용하여 일관성 보장
  switch (currency) {
    case "USD":
      // USD는 소수점 2자리까지 표시 (센트 단위)
      return `$${amount.toFixed(2)}`;
    case "JPY":
      // 일본 엔은 정수로 표시
      return `¥${Math.round(amount)}`;
    case "CNY":
      // 중국 위안은 정수로 표시
      return `¥${Math.round(amount)}`;
    case "TWD":
      // 대만 달러는 정수로 표시
      return `NT$${Math.round(amount)}`;
    case "KRW":
      // 한국 원은 정수로 표시하고 천 단위 구분자 추가
      return `₩${Math.round(amount).toLocaleString()}`;
    default:
      return `${Math.round(amount)}`;
  }
}; 